import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  EditableSlotGroup,
  RichTextContent,
  useProperty,
  useSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { btnPrimary, hasValue, linkTarget } from '../utils/content';

const photoDef: SlotGroupDefinition = {
  label: 'Neighborhood Photo',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'neighborhood_pois[0].poi_featured_image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'neighborhood_pois[0].poi_name' },
  },
  sourceBinding: {
    collection: 'neighborhood_pois',
    defaultIndex: 0,
    fieldMappings: { image: 'poi_featured_image', alt: 'poi_name' },
  },
  generator: {
    prompt: 'A photo that captures the local food, drink, or nightlife scene near this property, or a nearby landmark.',
    context: 'Square photo beside the "Live at the crossroads" neighborhood intro on the home page.',
  },
};

const ctaDef: SlotGroupDefinition = {
  label: 'Neighborhood Button',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/neighborhood' },
    label: { label: 'Button Text', type: 'text', defaultValue: 'Explore the Neighborhood' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

export function NeighborhoodSplit() {
  const photo = useSlotGroup('neighborhoodsplit.photo', photoDef);
  const cta = useSlotGroup('neighborhoodsplit.cta', ctaDef);
  const property = useProperty() as unknown as { photo_gallery?: Array<{ image?: string | null; alt_text?: string | null }> };

  // POIs often lack photos; borrow a later gallery image rather than leave a blank tile.
  const fallback = property?.photo_gallery?.[14] ?? property?.photo_gallery?.[0];
  const imageUrl = hasValue(photo.image) ? photo.image : (fallback?.image ?? '');
  const imageAlt = hasValue(photo.image) ? photo.alt : fallback?.alt_text;

  return (
    <section className="bg-background py-14 md:py-20">
      <div className="mx-auto grid max-w-[1180px] items-center gap-10 px-6 md:grid-cols-2 md:gap-12 lg:gap-20 lg:px-10">
        <EditableSlotGroup groupPath="neighborhoodsplit.photo" definition={photoDef}>
          <div className="js-reveal-img js-zoom relative mx-auto aspect-square w-full max-w-[480px] overflow-hidden bg-muted md:max-w-none">
            {imageUrl ? (
              <VibesitesImage
                url={imageUrl}
                alt={hasValue(imageAlt) ? imageAlt : ''}
                sizes="(min-width: 768px) 45vw, 100vw"
                className="h-full w-full object-cover"
              />
            ) : (
              <div className="h-full w-full bg-gradient-to-br from-secondary to-accent" />
            )}
            <span aria-hidden className="js-frame-line pointer-events-none absolute inset-[11%] border border-white/70" />
          </div>
        </EditableSlotGroup>

        <div>
          <RichTextContent
            path="neighborhoodsplit.heading"
            as="h2"
            className="js-reveal js-d1 js-heading text-[30px] !font-normal text-secondary sm:text-[36px] lg:text-[40px]"
          />
          <RichTextContent path="neighborhoodsplit.body" as="div" className="js-reveal js-d2 js-copy mt-6 text-foreground" />
          <EditableSlotGroup groupPath="neighborhoodsplit.cta" definition={ctaDef} className="js-reveal js-d3">
            <a href={cta.url || '/neighborhood'} target={linkTarget(cta.url, cta._target)} className={`${btnPrimary} mt-9`}>
              {cta.label || 'Explore the Neighborhood'}
            </a>
          </EditableSlotGroup>
        </div>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'NeighborhoodSplit',
  displayName: 'Neighborhood Intro',
  tokens: {
    'neighborhoodsplit.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue:
        '<p>Live at the crossroads of <span data-type="field-token" data-field="property_address_city"></span></p>',
    },
    'neighborhoodsplit.body': {
      label: 'Body',
      type: 'richtext',
      defaultField: 'property_home_neighborhood_blurb',
      suggestedFields: ['property_home_neighborhood_blurb', 'property_neighborhood_blurb'],
      defaultValue:
        '<p>Dining, coffee, shopping, and the outdoors are all moments from home. Spend your weekends exploring local trails and favorite spots, and come home to everything that makes <span data-type="field-token" data-field="property_address_city"></span> feel like yours.</p>',
    },
  },
  slotGroups: {
    'neighborhoodsplit.photo': photoDef,
    'neighborhoodsplit.cta': ctaDef,
  },
};

(NeighborhoodSplit as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default NeighborhoodSplit;
